<template>
  <PageSection font="medium">
    <SectionGroup gap="medium">
      <SectionBox>
        <BasicSection appearance="heading">
          <PageTitle title="Специалисты">
            <ActiveCompanySwitcher />
          </PageTitle>
        </BasicSection>

        <BasicTabs v-if="tabs" :tabs="tabs" appearance="primary" />

        <BasicSection appearance="search-panel">
          <SearchPanel
            :indicator-visible="hasCustomizedFilters"
            @openFilters="openModalFilters"
          >
            <template #primary>
              <SearchInput
                v-model="state.query"
                @update:modelValue="scheduleListUpdate"
                type="search"
                placeholder="Поиск"
              >
                <template #hint>
                  <HotPointHint v-if="hint" @close="markHintAsRead">
                    <template #title>{{ hint.title }}</template>
                    <template #default>
                      {{ hint.description }}
                    </template>
                  </HotPointHint>
                </template>
              </SearchInput>
            </template>
            <template #secondary>
              <BasicSelect
                v-model="state.order"
                @update:modelValue="scheduleListUpdate"
                :options="filters.options.order"
              />
            </template>
          </SearchPanel>

          <TransitionExpand v-if="!mediaQueries.mobile">
            <SearchFilters
              v-if="hasCustomizedFilters"
              :key="hasCustomizedFilters"
            >
              <SectionGroup gap="medium">
                <SelectedFilters
                  :items="filters.selectedList"
                  title="Выбранные вами фильтры:"
                  :max="2"
                  @remove="filters.resetOne($event), scheduleListUpdate()"
                />

                <FiltersControls
                  appearance="search-panel"
                  @save="saveFilters(filters.items)"
                  @reset="filters.reset(), scheduleListUpdate()"
                />
              </SectionGroup>
            </SearchFilters>
          </TransitionExpand>

          <TransitionExpand>
            <SearchTotal
              appearance="search-panel"
              v-model="searchResultsTitle"
            />
          </TransitionExpand>
        </BasicSection>
      </SectionBox>

      <PromotionCard
        v-if="mediaQueries.mobile && promoVacancy"
        appearance="resumes"
        :promo="promoVacancy"
      />

      <GuideCard v-if="mediaQueries.mobile" />

      <ResourceAction
        ref="modalFiltersAction"
        :action="action"
        :debounce="400"
        @success="onModalRequestSuccess"
        v-slot="{ isLoading: isModalLoading }"
      >
        <PaginatedResource
          ref="listResource"
          v-model="currentResumes"
          :action="action"
          :params="params"
          :debounce="400"
          @success="onListRequestSuccess"
          v-slot="{ isLoading }"
        >
          <TransitionExpand>
            <WithPagination
              :params="mappedParams"
              @click="onPagination"
              :key="isLoading"
              ref="scrollTarget"
            >
              <SectionGroup gap="medium">
                <template v-if="isLoading || isModalLoading">
                  <SectionBox
                    v-for="(_, index) in Array(
                      currentResumes.list.length < 3
                        ? 3
                        : currentResumes.list.length,
                    )"
                    :key="index"
                  >
                    <BasicSection appearance="horizontal-card">
                      <ResumeCardSkeleton />
                    </BasicSection>
                  </SectionBox>
                </template>

                <template v-if="!isLoading && !isModalLoading">
                  <BasicNotification
                    v-if="notificationInfo && notificationInfo.hasNotice"
                    appearance="alarm"
                    icon="alarm"
                  >
                    <span
                      >Доступные вам открытия контактов почти закончились.
                    </span>
                    <a target="_blank" :href="notificationInfo.prolongationLink"
                      >Продлить доступ</a
                    >
                  </BasicNotification>
                  <template v-if="currentResumes.list.length !== 0">
                    <template v-for="(resume, index) in currentResumes.list">
                      <EmotionalFeedback
                        v-if="index === 10 && showEmotionalFeedback"
                        :title="emotionalFeedback.formTitle"
                        :feature="emotionalFeedback.alias"
                        :key="index"
                        @close="hasFeedback = true"
                      />
                      <SectionBox :key="resume.id">
                        <BasicSection appearance="always-same-paddings">
                          <ResumeCard
                            :resume="resume"
                            @invite="markAsInvited(resume)"
                          />
                        </BasicSection>
                      </SectionBox>
                    </template>
                  </template>

                  <SectionBox
                    v-else-if="
                      currentResumes.meta.totalResults === 0 &&
                      currentResumes.list.length === 0
                    "
                  >
                    <BasicSection>
                      <NoContent
                        :title="$options.noContent.title"
                        :description="$options.noContent.description"
                        :icon="$options.noContent.icon"
                        :actions="$options.noContent.actions"
                      />
                    </BasicSection>
                  </SectionBox>

                  <SectionBox
                    v-if="
                      currentResumes.limitedAccess &&
                      currentResumes.meta.totalResults !== 0
                    "
                  >
                    <LimitedAccessCard
                      :limited-access="currentResumes.limitedAccess"
                    />
                  </SectionBox>
                </template>
              </SectionGroup>
            </WithPagination>
          </TransitionExpand>
        </PaginatedResource>
      </ResourceAction>
    </SectionGroup>

    <VuePortal target="[data-list-sidebar]">
      <ListPageSidebar v-if="!mediaQueries.mobile">
        <template #beforeFilters>
          <PageSection v-if="promoVacancy" font="medium">
            <PromotionCard appearance="resumes" :promo="promoVacancy" />
          </PageSection>
          <PageSection font="medium">
            <GuideCard />
          </PageSection>
          <PageSection v-if="!visitor.isGuest" font="medium">
            <SectionBox>
              <BasicSection appearance="sidebar-filters">
                <CollapsibleSection
                  title="Сохранённые фильтры"
                  :counter="savedFilters.items.length"
                >
                  <SavedFiltersWidget @click="loadSavedFilter" />
                </CollapsibleSection>
              </BasicSection>
            </SectionBox>
          </PageSection>
        </template>
        <template #filters>
          <ResumeFilters
            :filters="filters.items"
            :options="filters.options"
            :dynamic-options="filters.dynamicOptions"
            :groups="groups"
            @change="scheduleListUpdate"
          >
            <template #controls>
              <FiltersControls
                v-if="hasCustomizedFilters"
                @save="saveFilters(filters.items)"
                @reset="filters.reset(), scheduleListUpdate()"
              />
            </template>
          </ResumeFilters>
        </template>
      </ListPageSidebar>
    </VuePortal>
    <VuePortal target="[data-subscribe]">
      <PageSection>
        <NewsSubscribe
          v-if="!visitor.isGuest"
          :show-subscribe="showSubscribe"
        />
      </PageSection>
    </VuePortal>

    <TransitionFade body-scroll-lock>
      <FiltersModal
        v-if="filtersModalVisible"
        :saved-filters-description="$options.savedFiltersDescription"
        @close="closeModalFilters"
        @loadSavedFilter="loadSavedFilter($event), closeModalFilters()"
        @saveFilters="saveFilters"
      >
        <template #filters="{ filters: items, options, dynamicOptions }">
          <ResumeFilters
            :filters="items"
            :options="options"
            :dynamic-options="dynamicOptions"
            :groups="groups"
            @change="onModalFiltersUpdate"
          />
        </template>

        <template #submit-button="{ filters, dynamicOptions }">
          <BasicButton
            @click="applyModalFilters({ filters, dynamicOptions })"
            appearance="primary"
            full-width
            size="xl"
            >{{ applyFiltersTitle }}</BasicButton
          >
        </template>
      </FiltersModal>
    </TransitionFade>
  </PageSection>
</template>

<script>
import { ResumesService } from 'src/services/resumes.js';
import { getHint, markHintAsRead } from 'src/utils/featureHints.js';
import {
  getFormattedNumber,
  getRussianPluralDeclension,
} from 'src/utils/translation.js';

import ActiveCompanySwitcher from 'src/components/ActiveCompanySwitcher/ActiveCompanySwitcher.vue';
import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicNotification from 'src/components/BasicNotification/BasicNotification.vue';
import CollapsibleSection from 'src/components/CollapsibleSection/CollapsibleSection.vue';
import EmotionalFeedback from 'src/components/EmotionalFeedback/EmotionalFeedback.vue';
import FiltersControls from 'src/components/filters/FiltersControls/FiltersControls.vue';
import FiltersModal from 'src/components/filters/FiltersModal/FiltersModal.vue';
import SavedFiltersWidget from 'src/components/filters/SavedFiltersWidget/SavedFiltersWidget.vue';
import SearchFilters from 'src/components/filters/SearchFilters/SearchFilters.vue';
import SelectedFilters from 'src/components/filters/SelectedFilters/SelectedFilters.vue';
import BasicSelect from 'src/components/form/BasicSelect/BasicSelect.vue';
import SearchInput from 'src/components/form/SearchInput/SearchInput.vue';
import GuideCard from 'src/components/GuideCard/GuideCard.vue';
import HotPointHint from 'src/components/HotPointHint/HotPointHint.vue';
import ListPageSidebar from 'src/components/ListPageSidebar/ListPageSidebar.vue';
import NewsSubscribe from 'src/components/NewsSubscribe/NewsSubscribe.vue';
import PageTitle from 'src/components/PageTitle/PageTitle.vue';
import PaginatedResource from 'src/components/pagination/PaginatedResource/PaginatedResource.vue';
import WithPagination from 'src/components/pagination/WithPagination/WithPagination.vue';
import NoContent from 'src/components/placeholders/NoContent/NoContent.vue';
import PromotionCard from 'src/components/PromotionCard/PromotionCard.vue';
import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
import LimitedAccessCard from 'src/components/resumes/LimitedAccessCard/LimitedAccessCard.vue';
import ResumeCard from 'src/components/resumes/ResumeCard/ResumeCard.vue';
import ResumeCardSkeleton from 'src/components/resumes/ResumeCardSkeleton/ResumeCardSkeleton.vue';
import ResumeFilters from 'src/components/resumes/ResumeFilters/ResumeFilters.vue';
import SearchPanel from 'src/components/search/SearchPanel/SearchPanel.vue';
import SearchTotal from 'src/components/SearchTotal/SearchTotal.vue';
import SectionBox from 'src/components/SectionBox/SectionBox.vue';
import BasicSection from 'src/components/sections/BasicSection/BasicSection.vue';
import PageSection from 'src/components/sections/PageSection/PageSection.vue';
import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';
import BasicTabs from 'src/components/tabs/BasicTabs/BasicTabs.vue';
import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';
import TransitionFade from 'src/components/transitions/TransitionFade/TransitionFade.vue';
import VuePortal from 'src/components/VuePortal/VuePortal.vue';

import noContentIconSrc from 'images/no_content.icon.search.png';

const resumesDeclension = getRussianPluralDeclension(
  'Найден ${0} специалист',
  'Найдено ${0} специалиста',
  'Найдено ${0} специалистов',
);
const unauthResumesDeclension = getRussianPluralDeclension(
  'Найден ${0} специалист, еще ${1} после <a href=${2}>авторизации</a>',
  'Найдено ${0} специалиста, еще ${1} после <a href=${2}>авторизации</a>',
  'Найдено ${0} специалистов, еще ${1} после <a href=${2}>авторизации</a>',
);

const savedFiltersDescription = `
    Сохраненные фильтры помогают быстрее находить нужных специалистов.
    Чтобы сохранить фильтр, установите нужные параметры поиска и нажмите на кнопку «Сохранить фильтр».
  `;

const submitButtonDeclension = getRussianPluralDeclension(
  'Показать ${0} специалиста',
  'Показать ${0} специалиста',
  'Показать ${0} специалистов',
);
const RESUME_SLUG = 'resumes-new-full-text-search';

export default {
  name: 'ResumeListPage',
  components: {
    PromotionCard,
    GuideCard,
    BasicNotification,
    SearchTotal,
    NoContent,
    ResumeCardSkeleton,
    ResumeFilters,
    LimitedAccessCard,
    ResumeCard,
    FiltersModal,
    TransitionFade,
    ResourceAction,
    SavedFiltersWidget,
    CollapsibleSection,
    ListPageSidebar,
    VuePortal,
    WithPagination,
    PaginatedResource,
    FiltersControls,
    SelectedFilters,
    SectionGroup,
    SearchFilters,
    TransitionExpand,
    BasicSelect,
    SearchInput,
    SearchPanel,
    BasicTabs,
    BasicButton,
    PageTitle,
    BasicSection,
    SectionBox,
    PageSection,
    NewsSubscribe,
    EmotionalFeedback,
    ActiveCompanySwitcher,
    HotPointHint,
  },
  noContent: {
    title: 'Специалисты не найдены',
    description:
      'Не удалось найти специалистов по заданным параметрам. Попробуйте изменить условия поиска.',
    icon: {
      src: noContentIconSrc,
      width: '77px',
      height: '77px',
    },
  },
  savedFiltersDescription,
  inject: ['mediaQueries', 'visitor', 'filters', 'savedFilters'],
  props: {
    tabs: Array,
    resumes: Object,
    query: String,
    order: String,
    groups: Array,
    emotionalFeedback: Object,
    notificationInfo: Object,
    suggest: Object,
    promoVacancy: Object,
  },
  provide() {
    return {
      replaceDefaultJobInviteParams: this.replaceDefaultJobInviteParams,
    };
  },
  data() {
    const { resumes, query, order, suggest } = this;

    return {
      state: {
        query,
        order,
      },
      currentResumes: resumes,
      modalResumes: null,
      filtersModalVisible: false,
      hasFeedback: false,
      currentSuggest: suggest,
      hint: null,
    };
  },
  async mounted() {
    this.hint = await getHint(RESUME_SLUG);
  },
  methods: {
    action: ResumesService.getList,
    scheduleListUpdate() {
      this.$refs.listResource.loadAndReplace(this.params);
    },
    onListRequestSuccess({ data, params }) {
      ResumesService.syncLocation(params);
      if (Object.prototype.hasOwnProperty.call(data, 'suggest')) {
        this.currentSuggest = data.suggest;
      }
    },
    onPagination() {
      this.$refs.scrollTarget.$el.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'auto',
      });
    },
    saveFilters(filters) {
      this.savedFilters.save({ ...this.params, ...filters });
    },
    loadSavedFilter({ query, order, filters, filtersData }) {
      if (query) this.state.query = query;
      if (order) this.state.order = order;
      this.filters.set({ filters, dynamicOptions: filtersData });
      this.scheduleListUpdate();
    },
    openModalFilters() {
      this.filtersModalVisible = true;
      this.modalResumes = this.currentResumes;
    },
    closeModalFilters() {
      this.$refs.modalFiltersAction.cancel();
      this.filtersModalVisible = false;
    },
    onModalFiltersUpdate(filters) {
      this.$refs.modalFiltersAction.dispatch({
        ...this.baseParams,
        ...filters,
      });
    },
    onModalRequestSuccess({ data }) {
      if (this.filtersModalVisible) {
        this.modalResumes = data;
      } else {
        this.currentResumes = data;
      }
    },
    applyModalFilters({ filters, dynamicOptions }) {
      this.filtersModalVisible = false;
      this.filters.set({ filters, dynamicOptions });
      this.currentResumes = this.modalResumes;
      const params = { ...this.baseParams, ...filters };
      ResumesService.syncLocation(params);
    },
    showSubscribe({ isHr, hasSubscribe }) {
      return !hasSubscribe && isHr;
    },
    markAsInvited(resume) {
      // eslint-disable-next-line
      resume.jobInviteButton.state = 'already_sent';
    },
    replaceDefaultJobInviteParams({ vacancy, message }) {
      if (!Array.isArray(this.resumes.list)) return;

      this.resumes.list = this.resumes.list.map((resume) => {
        if (!resume.jobInviteButton) return resume;

        return {
          ...resume,
          jobInviteButton: {
            ...resume.jobInviteButton,
            vacancy,
            message,
          },
        };
      });
    },
    markHintAsRead() {
      markHintAsRead(this.hint.slug);
      this.hint = null;
    },
  },
  computed: {
    baseParams() {
      const { state } = this;
      const { query, order } = state;
      return { query, order };
    },
    params() {
      return { ...this.baseParams, ...this.filters.items };
    },
    resultsTitle() {
      const value = getFormattedNumber(this.currentResumes.meta.totalResults);
      const authTitle = resumesDeclension(value).replace('${0}', value);
      const unauthTitle = unauthResumesDeclension(value)
        .replace('${0}', value)
        .replace(
          '${1}',
          getFormattedNumber(this.currentResumes.meta.totalAuthOnly),
        )
        .replace('${2}', this.currentResumes.meta.authorizeUrl);

      return this.currentResumes.meta.totalAuthOnly === null
        ? authTitle
        : unauthTitle;
    },
    searchResultsTitle() {
      if (this.currentSuggest) {
        return `${this.resultsTitle}. Возможно, вы имели в виду  <a href=${this.currentSuggest.href}>${this.currentSuggest.text}</a>`;
      }
      return this.resultsTitle;
    },
    hasCustomizedFilters() {
      return this.filters.selectedList.length !== 0;
    },
    applyFiltersTitle() {
      if (!this.modalResumes) return null;
      const value = this.modalResumes.meta.totalResults;
      return submitButtonDeclension(value).replace('${0}', value);
    },
    mappedParams() {
      return ResumesService.getMappedParams(this.params);
    },
    showEmotionalFeedback() {
      return this.emotionalFeedback && !this.hasFeedback;
    },
  },
};
</script>
